import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Mail, Lock, User, AlertCircle, Gift, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import HireHubLogo from '../components/HireHubLogo';

const RegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [referrerName, setReferrerName] = useState('');
  const [validatingReferral, setValidatingReferral] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  // Capture referral code from URL on mount
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
      validateReferralCode(refCode.toUpperCase());
    }
  }, [searchParams]);

  const validateReferralCode = async (code) => {
    if (!code) return;
    setValidatingReferral(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/referral/validate-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      });
      
      if (response.ok) {
        const data = await response.json();
        setReferrerName(data.referrer_name);
      } else {
        setReferrerName('');
        toast.error('Invalid referral code');
      }
    } catch (err) {
      console.error('Error validating referral code:', err);
      toast.error('Could not validate referral code. Please try again.');
    } finally {
      setValidatingReferral(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await register({
        full_name: formData.full_name,
        email: formData.email,
        password: formData.password,
        referral_code: referralCode || undefined
      });
      
      if (referralCode && referrerName) {
        toast.success('Account created! Complete your first booking to unlock your $10 referral bonus!');
      } else {
        toast.success('Account created successfully!');
      }
      // GUEST CHECKOUT: Redirect back to listing if coming from booking intent
      const returnTo = location.state?.from?.pathname || '/dashboard';
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center mb-8">
          <HireHubLogo size="lg" />
        </Link>

        <Card className="border-slate-100 shadow-lg rounded-2xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="font-heading text-2xl">Create your account</CardTitle>
            <CardDescription>Join HireHub Australia today</CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {/* Referral Bonus Banner */}
            {referralCode && referrerName && (
              <div className="flex items-center gap-3 p-3 mb-4 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl" data-testid="referral-banner">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Gift className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-emerald-800">
                    <CheckCircle className="w-4 h-4 inline mr-1 text-emerald-600" />
                    {referrerName} invited you!
                  </p>
                  <p className="text-xs text-emerald-700">Complete your first booking to get $10 credit</p>
                </div>
              </div>
            )}
            
            {validatingReferral && (
              <div className="flex items-center justify-center p-3 mb-4 bg-slate-50 rounded-lg">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-200 border-t-[#004B8D] mr-2"></div>
                <span className="text-sm text-slate-600">Validating referral...</span>
              </div>
            )}
            
            {error && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 text-red-700 rounded-lg text-sm">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="full_name">Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="full_name"
                    type="text"
                    placeholder="John Smith"
                    className="pl-10 h-12 rounded-lg"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                    data-testid="fullname-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    className="pl-10 h-12 rounded-lg"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10 h-12 rounded-lg"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="password-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10 h-12 rounded-lg"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              {/* Referral Code Field */}
              <div>
                <Label htmlFor="referralCode">Referral Code (optional)</Label>
                <div className="relative mt-1">
                  <Gift className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="referralCode"
                    type="text"
                    placeholder="e.g., HH0D0206E13C"
                    className={`pl-10 h-12 rounded-lg uppercase ${
                      referrerName ? 'border-emerald-500 bg-emerald-50/50' : ''
                    }`}
                    value={referralCode}
                    onChange={(e) => {
                      const code = e.target.value.toUpperCase();
                      setReferralCode(code);
                      if (code.length >= 8) {
                        validateReferralCode(code);
                      } else {
                        setReferrerName('');
                      }
                    }}
                    data-testid="referral-code-input"
                  />
                  {referrerName && (
                    <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500" />
                  )}
                </div>
                {referralCode && !validatingReferral && !referrerName && referralCode.length >= 8 && (
                  <p className="text-xs text-red-500 mt-1" data-testid="referral-error">
                    Invalid referral code
                  </p>
                )}
                {referrerName && (
                  <p className="text-xs text-emerald-600 mt-1" data-testid="referral-valid">
                    Valid code! You'll both get $10 after your first booking.
                  </p>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#004B8D] hover:bg-[#003865] h-12 rounded-xl font-medium"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-slate-500">or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 rounded-xl border-slate-200"
              onClick={handleGoogleSignup}
              data-testid="google-signup-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <p className="text-center text-sm text-slate-600 mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-[#004B8D] font-medium hover:underline">
                Sign in
              </Link>
            </p>

            <p className="text-center text-xs text-slate-500 mt-4">
              By creating an account, you agree to our{' '}
              <Link to="/legal/terms" className="underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/legal/privacy" className="underline">Privacy Policy</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
